/**
 * 용량 룰 약사 검수 관리자 API 서비스 (8종).
 *
 * 경로·body·응답 필드는 계획 §3.0 계약(백엔드 정본)을 그대로 따른다.
 * 각 메서드는 공통 래퍼 `{ error, data?, message? }`를 언랩하지 않고 그대로 반환한다
 * (성공 판정 `error === 0`은 호출부/훅에서 한 곳에서 수행 — PharmacyAccess 관행).
 */
import axios from "api/axiosInstance";
import {
  AdminDosePromptTextsData,
  AdminDoseRuleDetail,
  AdminDoseRuleListData,
  AdminDoseRuleNote,
  AdminDoseRuleStats,
  AdminDoseRuleVersionDetail,
  AdminDoseRuleVersionListItem,
  DoseRuleApiResponse,
  FindDoseRulesQuery,
  FromStep,
  ReextractResult,
  UpdateDoseRuleStatusBody,
  UpdateDoseRuleStatusResult,
} from "./types";

const BASE = "/api/v1/admin/dose-rules";

/**
 * 재추출 개별 timeout(ms).
 * axios 기본 10초(`axiosInstance.ts`)로는 재추출 요청이 걸릴 때 원인 파악이 어렵다.
 * 잡 생성만 하는 API지만 여유 있게 개별 지정한다(계획 C-11).
 */
const REEXTRACT_TIMEOUT_MS = 60000;

export const doseRuleAdminApi = {
  /** GET /api/v1/admin/dose-rules — 목록(서버 offset 페이징). */
  list: async (
    query?: FindDoseRulesQuery
  ): Promise<DoseRuleApiResponse<AdminDoseRuleListData>> => {
    const res = await axios.get<DoseRuleApiResponse<AdminDoseRuleListData>>(BASE, {
      params: query,
    });
    return res.data;
  },

  /** GET /api/v1/admin/dose-rules/:ruleId — 상세. */
  detail: async (ruleId: string): Promise<DoseRuleApiResponse<AdminDoseRuleDetail>> => {
    const res = await axios.get<DoseRuleApiResponse<AdminDoseRuleDetail>>(
      `${BASE}/${encodeURIComponent(ruleId)}`
    );
    return res.data;
  },

  /** PATCH /api/v1/admin/dose-rules/:ruleId/status — 승인(active)/반려(archived). */
  updateStatus: async (
    ruleId: string,
    body: UpdateDoseRuleStatusBody
  ): Promise<DoseRuleApiResponse<UpdateDoseRuleStatusResult>> => {
    const res = await axios.patch<DoseRuleApiResponse<UpdateDoseRuleStatusResult>>(
      `${BASE}/${encodeURIComponent(ruleId)}/status`,
      body
    );
    return res.data;
  },

  /**
   * POST /api/v1/admin/dose-rules/:ruleId/reextract — 재추출 잡 요청.
   * 바디 `{ fromStep }`(신설). 생략하면 `step2`(현행 전체 재실행). 옛 서버는 바디를 무시한다.
   */
  reextract: async (
    ruleId: string,
    body: { fromStep: FromStep } = { fromStep: "step2" }
  ): Promise<DoseRuleApiResponse<ReextractResult>> => {
    const res = await axios.post<DoseRuleApiResponse<ReextractResult>>(
      `${BASE}/${encodeURIComponent(ruleId)}/reextract`,
      body,
      { timeout: REEXTRACT_TIMEOUT_MS }
    );
    return res.data;
  },

  /**
   * GET /api/v1/admin/dose-rules/:ruleId/versions — 버전 목록(현재 행 포함).
   * 서버가 `ORDER BY version DESC`로 준다. 0건이면 봉투 `error: 6110`.
   */
  versions: async (
    ruleId: string
  ): Promise<DoseRuleApiResponse<AdminDoseRuleVersionListItem[]>> => {
    const res = await axios.get<DoseRuleApiResponse<AdminDoseRuleVersionListItem[]>>(
      `${BASE}/${encodeURIComponent(ruleId)}/versions`
    );
    return res.data;
  },

  /**
   * GET /api/v1/admin/dose-rules/:ruleId/notes — 검수 메모·시스템 노트 전량.
   * 쿼리 `kind`를 **넘기지 않는다** — 선별은 호출부가 `author`로 한다(계획 E-1).
   */
  notes: async (ruleId: string): Promise<DoseRuleApiResponse<AdminDoseRuleNote[]>> => {
    const res = await axios.get<DoseRuleApiResponse<AdminDoseRuleNote[]>>(
      `${BASE}/${encodeURIComponent(ruleId)}/notes`
    );
    return res.data;
  },

  /**
   * GET /api/v1/admin/dose-rules/versions/:versionRowId — 버전 행(은퇴 포함) 당시 본문.
   * versionRowId = `rules_json[].id`(dose_rules 행 PK). 09-25 룰 버전 UI Phase 2 항목 10
   * 공유분으로, 이 Phase가 소유한다(D-12·D-21). 없는 id·비정수는 봉투 `error: 6110`.
   */
  versionDetail: async (
    versionRowId: number
  ): Promise<DoseRuleApiResponse<AdminDoseRuleVersionDetail>> => {
    const res = await axios.get<DoseRuleApiResponse<AdminDoseRuleVersionDetail>>(
      `${BASE}/versions/${versionRowId}`
    );
    return res.data;
  },

  /** GET /api/v1/admin/dose-rules/stats — 진척 집계. */
  stats: async (): Promise<DoseRuleApiResponse<AdminDoseRuleStats>> => {
    const res = await axios.get<DoseRuleApiResponse<AdminDoseRuleStats>>(`${BASE}/stats`);
    return res.data;
  },

  /**
   * GET /api/v1/admin/dose-rules/prompt-texts?sha256=a,b,c — 보낸 전문 원문.
   * sha256은 **쉼표 결합 단일 쿼리**로 보낸다(axios 배열 직렬화 회피 — 백엔드가 split).
   * 최대 10개·각 64자 소문자 hex, 위반은 봉투가 아닌 Nest 기본 HTTP 400.
   * 응답 data `{ texts: [{ sha256, body, byteLength }], missing }`.
   */
  getPromptTexts: async (
    sha256: string[]
  ): Promise<DoseRuleApiResponse<AdminDosePromptTextsData>> => {
    const res = await axios.get<DoseRuleApiResponse<AdminDosePromptTextsData>>(
      `${BASE}/prompt-texts`,
      { params: { sha256: sha256.join(",") } }
    );
    return res.data;
  },
};

export { REEXTRACT_TIMEOUT_MS };
